import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, Download, ZoomIn, ZoomOut, RotateCcw } from "lucide-react";

/**
 * Full-screen image lightbox with zoom (buttons + wheel), pan (drag when zoomed),
 * and download. Close via X, Esc, or clicking the backdrop.
 */
export default function ImageLightbox({ src, alt = "", downloadName, onClose }) {
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const dragging = useRef(null);

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);
  const zoomIn = useCallback(() => setScale((s) => Math.min(6, +(s + 0.5).toFixed(2))), []);
  const zoomOut = useCallback(() => setScale((s) => {
    const next = Math.max(1, +(s - 0.5).toFixed(2));
    if (next === 1) { setTx(0); setTy(0); }
    return next;
  }), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, zoomIn, zoomOut, reset]);

  const onWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn(); else zoomOut();
  };

  const onPointerDown = (e) => {
    if (scale <= 1) return;
    dragging.current = { startX: e.clientX - tx, startY: e.clientY - ty };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    setTx(e.clientX - dragging.current.startX);
    setTy(e.clientY - dragging.current.startY);
  };
  const onPointerUp = () => { dragging.current = null; };

  const download = async () => {
    try {
      const res = await fetch(src, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName || `nitewire-${Date.now()}.${(blob.type.split("/")[1] || "png").split(";")[0]}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in new tab
      window.open(src, "_blank", "noopener");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-obsidian/95 flex items-center justify-center"
      onClick={onClose}
      onWheel={onWheel}
      data-testid="image-lightbox"
    >
      <div
        className="absolute inset-0 flex items-center justify-center overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        style={{ cursor: scale > 1 ? (dragging.current ? "grabbing" : "grab") : "zoom-in" }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          onDoubleClick={() => (scale === 1 ? setScale(2) : reset())}
          style={{
            transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
            transition: dragging.current ? "none" : "transform 0.15s ease-out",
            maxWidth: "92vw",
            maxHeight: "88vh",
            userSelect: "none",
          }}
          data-testid="lightbox-img"
        />
      </div>

      {/* Toolbar */}
      <div
        className="absolute top-4 left-1/2 -translate-x-1/2 flex items-center gap-1 border-2 border-acid bg-obsidian/90 backdrop-blur-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={zoomOut}
          className="p-3 text-white/80 hover:bg-acid hover:text-obsidian transition-colors"
          title="Zoom out (-)"
          data-testid="lightbox-zoom-out"
        >
          <ZoomOut className="w-4 h-4" />
        </button>
        <span className="font-mono text-[11px] text-acid px-2 min-w-[42px] text-center" data-testid="lightbox-scale">
          {Math.round(scale * 100)}%
        </span>
        <button
          type="button"
          onClick={zoomIn}
          className="p-3 text-white/80 hover:bg-acid hover:text-obsidian transition-colors"
          title="Zoom in (+)"
          data-testid="lightbox-zoom-in"
        >
          <ZoomIn className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={reset}
          className="p-3 text-white/60 hover:bg-acid hover:text-obsidian transition-colors border-l border-white/20"
          title="Reset (0)"
          data-testid="lightbox-reset"
        >
          <RotateCcw className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={download}
          className="p-3 text-white/80 hover:bg-acid hover:text-obsidian transition-colors border-l border-white/20"
          title="Download"
          data-testid="lightbox-download"
        >
          <Download className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          className="p-3 text-white/80 hover:bg-blaze hover:text-obsidian transition-colors border-l border-white/20"
          title="Close (Esc)"
          data-testid="lightbox-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-[10px] text-white/40 tracking-widest">
        SCROLL TO ZOOM · DOUBLE CLICK · DRAG WHEN ZOOMED · ESC TO CLOSE
      </div>
    </div>
  );
}
